/**
 * Store pickup availability
 * ---------------------------------------------------------------------------
 * Enhances the pickup drawer rendered by snippets/pickup-availability.liquid:
 *
 *   - sorts stores: available first, then nearest, then alphabetically
 *   - "Use my location" adds a distance and a directions link to each store.
 *     Geolocation is only ever requested from that click — never on page load —
 *     and the position is kept in localStorage on the shopper's own device.
 * Note: hiding a store by its stock level is deliberately NOT done here. Liquid's
 * store_availability carries no per-location quantity, so the theme cannot know it;
 * that rule is handled by an app instead.
 *
 * Implemented as a custom element so it re-applies automatically: the theme
 * swaps the whole pickup block on every variant change, and connectedCallback
 * runs again each time the new markup is inserted.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'askmario:pickup-location';
  var MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // forget a stored position after 30 days
  var EARTH_RADIUS_KM = 6371;

  function readStoredLocation() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.lat !== 'number' || typeof parsed.lng !== 'number') return null;
      if (parsed.ts && Date.now() - parsed.ts > MAX_AGE_MS) {
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch (err) {
      return null;
    }
  }

  function writeStoredLocation(lat, lng) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ lat: lat, lng: lng, ts: Date.now() }));
    } catch (err) {
      /* private browsing — distances still work for this page view */
    }
  }

  function clearStoredLocation() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      /* no-op */
    }
  }

  function toRadians(deg) {
    return (deg * Math.PI) / 180;
  }

  function haversineKm(lat1, lng1, lat2, lng2) {
    var dLat = toRadians(lat2 - lat1);
    var dLng = toRadians(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatDistance(km) {
    if (km < 1) return Math.round(km * 1000) + ' m away';
    if (km < 10) return km.toFixed(1) + ' km away';
    return Math.round(km) + ' km away';
  }

  // Normalised for tolerant name matching in the coordinate-override setting.
  function normalise(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  /**
   * Parses the "location|latitude|longitude" override lines from theme settings.
   * Keyed by both the numeric location id and the normalised location name, so a
   * merchant can use whichever they have to hand.
   */
  function parseCoordinateOverrides(raw) {
    var map = Object.create(null);
    String(raw || '')
      .split('\n')
      .forEach(function (line) {
        var parts = line.split('|');
        if (parts.length < 3) return;
        var key = parts[0].trim();
        var lat = parseFloat(parts[1]);
        var lng = parseFloat(parts[2]);
        if (!key || isNaN(lat) || isNaN(lng)) return;
        map[normalise(key)] = { lat: lat, lng: lng };
      });
    return map;
  }

  if (typeof window.HTMLElement !== 'function' || !window.customElements) return;

  class AskmarioPickupList extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === 'true') return;
      this.dataset.initialised = 'true';

      this.overrides = parseCoordinateOverrides(this.dataset.coordinateOverrides);
      this.storesEl = this.querySelector('.pickup-list__stores');
      this.emptyEl = this.querySelector('[data-pickup-empty]');
      this.controlsEl = this.querySelector('[data-pickup-location-controls]');
      this.promptEl = this.querySelector('[data-pickup-location-prompt]');
      this.actionsEl = this.querySelector('[data-pickup-location-actions]');
      this.errorEl = this.querySelector('[data-pickup-location-error]');
      this.rows = Array.prototype.slice.call(this.querySelectorAll('.pickup-location'));

      if (!this.storesEl || !this.rows.length) return;

      // Remember the server-rendered order so "Clear" can fall back to it predictably.
      this.rows.forEach(function (row, index) {
        row.dataset.originalOrder = String(index);
      });

      this.bindEvents();

      // Offer geolocation whenever at least one store has coordinates. Gating on "more than one
      // store" would hide it entirely while only one location has pickup enabled — and the
      // distance and directions are useful even for a single store.
      var self = this;
      var anyLocatable = this.rows.some(function (row) {
        return !!self.coordinatesFor(row);
      });
      if (this.controlsEl && anyLocatable && navigator.geolocation) {
        this.controlsEl.hidden = false;
      }

      var stored = readStoredLocation();
      if (stored) {
        this.applyLocation(stored.lat, stored.lng);
      } else {
        this.sortRows();
        this.updateEmptyState();
      }

    }

    bindEvents() {
      var self = this;

      Array.prototype.forEach.call(this.querySelectorAll('[data-pickup-request-location]'), function (button) {
        button.addEventListener('click', function () {
          self.requestLocation(button);
        });
      });

      var clearButton = this.querySelector('[data-pickup-clear-location]');
      if (clearButton) {
        clearButton.addEventListener('click', function () {
          clearStoredLocation();
          self.clearDistances();
          self.sortRows();
          self.updateLocationUi(false);
          self.updateEmptyState();
        });
      }
    }

    updateLocationUi(hasLocation) {
      if (this.promptEl) this.promptEl.hidden = hasLocation;
      if (this.actionsEl) this.actionsEl.hidden = !hasLocation;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    requestLocation(button) {
      var self = this;
      if (!navigator.geolocation) {
        this.showError('Your browser cannot share a location.');
        return;
      }

      var originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = 'Finding you…';
      this.showError('');

      var restore = function () {
        button.disabled = false;
        button.textContent = originalLabel;
      };

      navigator.geolocation.getCurrentPosition(
        function (position) {
          restore();
          writeStoredLocation(position.coords.latitude, position.coords.longitude);
          self.applyLocation(position.coords.latitude, position.coords.longitude);
        },
        function () {
          restore();
          self.showError("We couldn't get your location, so stores are listed by availability.");
          self.sortRows();
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
      );
    }

    coordinatesFor(row) {
      var override =
        this.overrides[normalise(row.dataset.locationId)] || this.overrides[normalise(row.dataset.locationName)];
      if (override) return override;

      var lat = parseFloat(row.dataset.lat);
      var lng = parseFloat(row.dataset.lng);
      if (isNaN(lat) || isNaN(lng)) return null;
      return { lat: lat, lng: lng };
    }

    applyLocation(lat, lng) {
      var self = this;

      this.rows.forEach(function (row) {
        var coords = self.coordinatesFor(row);
        var distanceEl = row.querySelector('[data-pickup-distance]');
        var directionsEl = row.querySelector('[data-pickup-directions]');

        if (!coords) {
          delete row.dataset.distance;
          if (distanceEl) distanceEl.hidden = true;
          if (directionsEl) directionsEl.hidden = true;
          return;
        }

        var km = haversineKm(lat, lng, coords.lat, coords.lng);
        row.dataset.distance = String(km);

        if (distanceEl) {
          distanceEl.textContent = formatDistance(km);
          distanceEl.hidden = false;
        }

        if (directionsEl) {
          directionsEl.href =
            'https://www.google.com/maps/dir/?api=1&origin=' +
            lat +
            ',' +
            lng +
            '&destination=' +
            coords.lat +
            ',' +
            coords.lng;
          directionsEl.hidden = false;
        }
      });

      this.sortRows();
      this.updateLocationUi(true);
      this.updateEmptyState();
    }

    clearDistances() {
      this.rows.forEach(function (row) {
        delete row.dataset.distance;
        var distanceEl = row.querySelector('[data-pickup-distance]');
        var directionsEl = row.querySelector('[data-pickup-directions]');
        if (distanceEl) distanceEl.hidden = true;
        if (directionsEl) directionsEl.hidden = true;
      });
      this.showError('');
    }

    // Available first, then nearest, then whatever order Shopify gave us.
    sortRows() {
      var sorted = this.rows.slice().sort(function (a, b) {
        var aAvailable = a.dataset.available === 'true';
        var bAvailable = b.dataset.available === 'true';
        if (aAvailable !== bAvailable) return aAvailable ? -1 : 1;

        var aDistance = parseFloat(a.dataset.distance);
        var bDistance = parseFloat(b.dataset.distance);
        var aHas = !isNaN(aDistance);
        var bHas = !isNaN(bDistance);
        if (aHas && bHas && aDistance !== bDistance) return aDistance - bDistance;
        if (aHas !== bHas) return aHas ? -1 : 1;

        return Number(a.dataset.originalOrder) - Number(b.dataset.originalOrder);
      });

      var fragment = document.createDocumentFragment();
      sorted.forEach(function (row) {
        fragment.appendChild(row);
      });
      this.storesEl.appendChild(fragment);
    }

    updateEmptyState() {
      if (!this.emptyEl) return;
      var visible = this.rows.some(function (row) {
        return !row.hidden;
      });
      this.emptyEl.hidden = visible;
    }

  }

  if (!window.customElements.get('askmario-pickup-list')) {
    window.customElements.define('askmario-pickup-list', AskmarioPickupList);
  }
})();
